// As peças da Central Inteligente IBS-CBS (000230/000231), da Pós RT.
//
// ---------------------------------------------------------------------------
// O que esta tela aprendeu com o CST e ClassTrib, duas semanas antes
// ---------------------------------------------------------------------------
// Lá a primeira versão despejava as 164 classificações abertas e a captura deu
// **20.541px** — 22,8 telas. Aqui o acervo é 12 vezes maior (2.070 dispositivos),
// então despejar não é uma opção ruim: é impossível. Três consequências, e as
// três são desenho:
//
//   · a lista é sempre RECORTADA (norma, tema ou termo) e PAGINADA, e o total
//     vem junto — lista paginada sem o total faz a tela mentir sobre o que existe;
//   · a linha da lista é uma LINHA, não um cartão: o uso real é procurar um
//     dispositivo pelo assunto, e lista densa de texto encontra melhor que grade
//     de cards (a cicatriz do IR Pro, 8.249px → 1.120px com o MESMO conteúdo);
//   · a hierarquia da norma (Livro › Título › Capítulo) nasce FECHADA.
//
// ---------------------------------------------------------------------------
// Cor
// ---------------------------------------------------------------------------
// Dentro de `painel-navy` só entra cor de marca ou fixa (`navy-ink`,
// `navy-mute`, `laranja-claro`, `white`) — token semântico ali clareia no escuro
// e some. Fora do navy, azul de INTERFACE é `acento`, nunca `azul`: `#102850`
// sobre a superfície escura dá 1,07:1, e foi isso que produziu 22 defeitos no
// Radar em 05/09.
import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { ArtigoDaLista, Norma, Tema } from "../lib/central";

// ===========================================================================
// A navegação da ferramenta — SUB-ROTA, nunca `useState`
// ===========================================================================
// O aluno volta pelo botão do navegador, compartilha o link de uma aba e
// recarrega sem cair na primeira. É a mesma razão de a rota da ferramenta ser
// derivada do slug: estado que deveria estar no endereço, guardado em memória, é
// uma tela que não se pode indicar a ninguém.
export type Aba = { para: string; rotulo: string; exato?: boolean };

export function AbasDaCentral({
  abas,
  atual,
  base,
  curso,
}: {
  abas: Aba[];
  /** O caminho DENTRO da ferramenta ("", "temas", "normas/lc-214"…). */
  atual: string;
  /** A raiz da ferramenta (`/ferramenta/central-ibs-cbs`). */
  base: string;
  /** O curso, que viaja na query e não pode se perder ao trocar de aba. */
  curso: string;
}) {
  const ativa = (a: Aba) =>
    a.exato ? atual === a.para : atual === a.para || atual.startsWith(`${a.para}/`);

  // O destino é ABSOLUTO, e isto custou um defeito que Victor viu na tela.
  //
  // A primeira versão usava `to={`./${a.para}`} relative="path"`, e `./temas`
  // com `relative="path"` **acrescenta** ao caminho atual em vez de trocar o
  // segmento. Clicar uma aba funcionava; clicar a segunda produzia
  // `/normas/temas`, a terceira `/normas/temas/mudancas`, e a tela travava na
  // primeira aba escolhida porque o despacho lê o PRIMEIRO segmento.
  //
  // A asserção que existia media "o endereço MUDOU" — e mudou mesmo, na
  // primeira vez. É a diferença entre medir uma transição e medir DUAS: o
  // defeito só existe a partir do segundo clique.
  const destino = (a: Aba) =>
    `${base}${a.para ? `/${a.para}` : ""}${curso ? `?curso=${encodeURIComponent(curso)}` : ""}`;

  return (
    <nav
      aria-label="Seções da Central"
      data-abas-da-central
      className="-mx-1 flex gap-1 overflow-x-auto pb-1"
    >
      {abas.map((a) => {
        const marcada = ativa(a);
        return (
          <Link
            key={a.para}
            to={destino(a)}
            aria-current={marcada ? "page" : undefined}
            data-aba={a.para || "inicio"}
            // A aba escolhida é `bg-acento text-acento-tinta` e NÃO
            // `bg-white text-navy-ink`: no CST essa segunda forma deu **1,13:1**
            // — branco sobre branco —, porque `--cf-navy-ink` é a tinta CLARA,
            // para usar SOBRE navy. Aqui o par troca junto com o tema.
            className={`min-h-11 shrink-0 rounded-full px-4 py-2 text-sm font-medium transition ${
              marcada
                ? "bg-acento text-acento-tinta"
                : "border border-borda bg-superficie text-tinta hover:bg-chip"
            }`}
          >
            {a.rotulo}
          </Link>
        );
      })}
    </nav>
  );
}

// ===========================================================================
// O chip de norma, e por que a IMPORTÂNCIA não vira filtro
// ===========================================================================
// A origem tem uma tela que filtra `importancia = 'alto'`. Medido em 14/09:
// **1.973 dos 2.070 artigos são 'alto'** — 95,3%. Um filtro que seleciona 95%
// não separa nada (é o filtro "Perito" do Mapa de Concursos pelo outro extremo,
// e aquele alcançava zero). O chip FICA, porque é dado da origem e o aluno o vê
// na ficha; o que não existe é a promessa de que ele filtra.
export function ChipDeNorma({ tipo, numero, nome }: { tipo: string; numero: string | null; nome: string }) {
  return (
    <span
      data-chip-norma
      className="inline-flex items-center rounded-full bg-chip px-2.5 py-0.5 text-xs font-medium text-tinta"
      title={nome}
    >
      {rotuloCurtoDaNorma(tipo, numero, nome)}
    </span>
  );
}

/** O nome CURTO da norma, o mesmo que a citação da IA usa. */
export function rotuloCurtoDaNorma(tipo: string, numero: string | null, nome: string): string {
  if (tipo === "lei_complementar") return `LC ${numero ?? ""}`.trim();
  return nome;
}

export function ChipDeTema({ nome }: { nome: string }) {
  return (
    <span className="inline-flex items-center rounded-full border border-borda px-2.5 py-0.5 text-xs text-cinza">
      {nome}
    </span>
  );
}

/** A trilha da hierarquia legal: Livro › Título › Capítulo › Seção › Subseção. */
export function Trilha({
  partes,
  className = "",
}: {
  partes: (string | null | undefined)[];
  className?: string;
}) {
  const vivas = partes.filter((p): p is string => !!p && p.trim() !== "");
  if (!vivas.length) return null;
  return (
    <p className={`text-xs text-cinza ${className}`} data-trilha-legal>
      {vivas.map((p, i) => (
        <span key={`${p}-${i}`}>
          {i > 0 && <span aria-hidden="true" className="mx-1 opacity-50">›</span>}
          {p}
        </span>
      ))}
    </p>
  );
}

// ===========================================================================
// A LINHA de um dispositivo na lista
// ===========================================================================
// Linha, não cartão. Com 2.070 dispositivos o gesto é procurar, e o cartão gasta
// 170px para mostrar 30 caracteres de título — foi o que levou a busca de CNAE do
// Radar a 8.521px com 50 resultados.
export function LinhaDeArtigo({
  artigo,
  base,
  mostrarNorma = true,
}: {
  artigo: ArtigoDaLista;
  /** o prefixo da rota da ferramenta, para o link do artigo */
  base: string;
  mostrarNorma?: boolean;
}) {
  return (
    <li data-artigo={artigo.id}>
      <Link
        to={`${base}/artigo/${artigo.id}`}
        className="block rounded-cf border border-transparent px-3 py-3 transition hover:border-borda hover:bg-chip focus-visible:border-acento"
      >
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="font-display text-sm font-bold text-titulo" data-rotulo>
            {artigo.rotulo ?? "Dispositivo"}
          </span>
          {mostrarNorma && (
            <ChipDeNorma tipo={artigo.norma_tipo} numero={artigo.norma_numero} nome={artigo.norma} />
          )}
        </div>
        <Trilha
          partes={[artigo.livro, artigo.titulo, artigo.capitulo, artigo.secao, artigo.subsecao]}
          className="mt-1"
        />
        {artigo.resumo && (
          <p className="mt-1.5 line-clamp-3 text-sm leading-relaxed text-tinta">{artigo.resumo}</p>
        )}
      </Link>
    </li>
  );
}

// ===========================================================================
// A ÁRVORE da norma — nasce FECHADA
// ===========================================================================
// Com todos os capítulos abertos a LC 214 sozinha desenha 598 linhas. A árvore
// agrupa pelo primeiro nível que EXISTE (medido na origem: `livro` está em 2.044
// dos 2.070, `titulo` em 1.894, `capitulo` em 1.800) e abre um nó por vez.
//
// Buscando, tudo abre: quem digitou um termo quer ver onde ele caiu, e um
// resultado escondido atrás de um triângulo fechado é um resultado que não
// existe para quem procura.
export function ArvoreDaNorma({
  artigos,
  base,
  buscando,
}: {
  artigos: ArtigoDaLista[];
  base: string;
  buscando: boolean;
}) {
  const grupos = useMemo(() => {
    // A CHAVE É O CAMINHO INTEIRO, e isto custou uma captura.
    //
    // A primeira versão agrupava pelo primeiro nível preenchido —
    // `capitulo || titulo || livro` — e a foto da LC 214 mostrou "CAPÍTULO I,
    // 43 dispositivos". A LC 214 tem três LIVROS, e cada um começa com um
    // Capítulo I: agrupar só pelo rótulo do capítulo FUNDE capítulos de livros
    // diferentes num grupo só, e o número ao lado passa a ser a soma de coisas
    // que não têm nada a ver uma com a outra.
    //
    // O rótulo "CAPÍTULO I" não é identidade — ele é o NOME da divisão dentro do
    // pai. É a mesma família do `rotulo` do artigo, que se repete 22 vezes
    // dentro da mesma norma (medido na origem): quem identifica é o caminho.
    //
    // E o caminho para NOS DOIS PRIMEIROS níveis preenchidos, que é outra coisa
    // que a captura decidiu. Com a hierarquia inteira na chave a árvore ficou
    // CERTA e inútil: 126 grupos para 300 artigos, 60 deles com UM dispositivo,
    // e a tela em 7.951px — a cicatriz do CST (20.541px) pelo caminho oposto,
    // fragmentando em vez de despejar.
    //
    // MEDIDO em 14/09, grupos por nível:
    //
    //   norma                  artigos   livro   +título   +capítulo   +seção
    //   LC 214 ..................  598      4       17         69        172
    //   Regulamento da CBS ......  620      7       35         82        186
    //   Regulamento do IBS ......  617      8       34         84        185
    //   LC 227 ..................  210      3       10         33         56
    //
    // Dois níveis dão de 10 a 35 grupos por norma — uma tela de índice. Três já
    // passam de 80, e a média cai para 8 artigos por grupo, que é menos do que
    // cabe numa olhada. Capítulo e seção não somem: eles estão na trilha de CADA
    // linha, que é onde importam para quem já abriu o grupo.
    const m = new Map<string, { partes: string[]; itens: ArtigoDaLista[] }>();
    for (const a of artigos) {
      const preenchidos = [a.livro, a.titulo, a.capitulo, a.secao, a.subsecao].filter(
        (p): p is string => !!p && p.trim() !== "",
      );
      // sem divisão nenhuma o artigo vai para um grupo nomeado, em vez de sumir
      const caminho = preenchidos.length ? preenchidos.slice(0, 2) : ["Sem divisão"];
      const chave = caminho.join(" › ");
      if (!m.has(chave)) m.set(chave, { partes: caminho, itens: [] });
      m.get(chave)!.itens.push(a);
    }
    return [...m.entries()];
  }, [artigos]);

  const [abertos, setAbertos] = useState<Set<string>>(new Set());
  const alternar = (chave: string) =>
    setAbertos((s) => {
      const novo = new Set(s);
      if (novo.has(chave)) novo.delete(chave);
      else novo.add(chave);
      return novo;
    });

  // Um grupo só: abrir é o certo, porque a lista inteira É o grupo e um
  // triângulo fechado escondendo tudo seria um clique sem escolha.
  const unico = grupos.length === 1;

  return (
    <div data-arvore-da-norma>
      {grupos.map(([chave, grupo]) => {
        const aberto = buscando || unico || abertos.has(chave);
        // O último nível é o TÍTULO do grupo; os de cima são a trilha em cima
        // dele. Repetir "LIVRO I › TÍTULO II › CAPÍTULO I" inteiro em negrito
        // numa lista de 60 grupos seria a mesma informação dita 60 vezes.
        const titulo = grupo.partes[grupo.partes.length - 1];
        const acima = grupo.partes.slice(0, -1);
        return (
          <section key={chave} className="border-b border-borda last:border-0" data-grupo={chave}>
            {!unico && (
              <button
                type="button"
                onClick={() => alternar(chave)}
                aria-expanded={aberto}
                className="flex min-h-11 w-full items-center justify-between gap-3 px-3 py-3 text-left hover:bg-chip"
              >
                <span className="min-w-0 flex-1">
                  {acima.length > 0 && (
                    <span className="block truncate text-xs text-cinza">{acima.join(" › ")}</span>
                  )}
                  <span className="block text-sm font-medium text-tinta">{titulo}</span>
                </span>
                <span className="shrink-0 text-xs tabular-nums text-cinza">
                  {grupo.itens.length} {grupo.itens.length === 1 ? "dispositivo" : "dispositivos"}
                </span>
                <span aria-hidden="true" className={`shrink-0 text-cinza transition ${aberto ? "rotate-90" : ""}`}>
                  ›
                </span>
              </button>
            )}
            {aberto && (
              <ul className="pb-2">
                {grupo.itens.map((a) => (
                  <LinhaDeArtigo key={a.id} artigo={a} base={base} mostrarNorma={false} />
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

// ===========================================================================
// Paginação — e ela DIZ o total
// ===========================================================================
export function Paginacao({
  total,
  offset,
  porPagina,
  aoIr,
}: {
  total: number;
  offset: number;
  porPagina: number;
  aoIr: (offset: number) => void;
}) {
  if (total <= porPagina) return null;
  const primeiro = offset + 1;
  const ultimo = Math.min(offset + porPagina, total);
  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3" data-paginacao>
      <p className="text-sm text-cinza tabular-nums">
        {primeiro}–{ultimo} de {total.toLocaleString("pt-BR")}
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={offset <= 0}
          onClick={() => aoIr(Math.max(0, offset - porPagina))}
          className="min-h-11 rounded-full border border-borda px-4 text-sm font-medium text-tinta disabled:opacity-40"
        >
          Anteriores
        </button>
        <button
          type="button"
          disabled={ultimo >= total}
          onClick={() => aoIr(offset + porPagina)}
          className="min-h-11 rounded-full border border-borda px-4 text-sm font-medium text-tinta disabled:opacity-40"
        >
          Próximos
        </button>
      </div>
    </div>
  );
}

// ===========================================================================
// O cartão de um TEMA e o de uma NORMA
// ===========================================================================
export function CartaoDeTema({ tema, base }: { tema: Tema; base: string }) {
  return (
    <Link
      to={`${base}/temas/${tema.slug}`}
      data-tema={tema.slug}
      className="surface porta flex min-h-[4.5rem] items-center justify-between gap-3 rounded-cf px-4 py-3"
    >
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium text-titulo">{tema.nome}</span>
        {tema.descricao && (
          <span className="mt-0.5 block truncate text-xs text-cinza">{tema.descricao}</span>
        )}
      </span>
      <span className="shrink-0 rounded-full bg-chip px-2.5 py-0.5 text-xs font-bold tabular-nums text-tinta">
        {tema.artigos}
      </span>
    </Link>
  );
}

export function CartaoDeNorma({ norma, base }: { norma: Norma; base: string }) {
  return (
    <Link
      to={`${base}/normas/${norma.slug}`}
      data-norma={norma.slug}
      className="surface porta block rounded-cf px-5 py-4"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-display text-base font-bold text-titulo">{norma.nome}</h3>
        <span className="shrink-0 text-xs tabular-nums text-cinza">
          {norma.artigos.toLocaleString("pt-BR")} dispositivos
        </span>
      </div>
      {norma.ementa && <p className="mt-1.5 line-clamp-2 text-sm text-tinta">{norma.ementa}</p>}
      <p className="mt-2 text-xs text-cinza">
        {[norma.orgao, norma.data_publicacao ? emData(norma.data_publicacao) : null]
          .filter(Boolean)
          .join(" · ")}
      </p>
    </Link>
  );
}

function emData(iso: string): string {
  const [a, m, d] = iso.split("-");
  return d && m && a ? `${d}/${m}/${a}` : iso;
}

// ===========================================================================
// A seção de leitura da ficha do artigo
// ===========================================================================
// `whitespace-pre-wrap` porque o texto da lei tem a quebra dele, e reflow o
// destruiria. E o TEXTO DA LEI e a LEITURA DA CASA ficam visualmente separados
// pela mesma razão que o indexador os separa no corpus: o que a lei diz e o que
// nós dizemos que ela significa são coisas diferentes, e o aluno leva a resposta
// para um cliente.
export function SecaoDeLeitura({
  titulo,
  children,
  destaque = false,
}: {
  titulo: string;
  children: ReactNode;
  destaque?: boolean;
}) {
  return (
    <section
      data-secao={titulo}
      className={`rounded-cf px-4 py-3.5 ${destaque ? "border border-borda bg-superficie-2" : "bg-chip"}`}
    >
      <h3 className="text-xs font-semibold tracking-[0.12em] text-cinza uppercase">{titulo}</h3>
      <div className="mt-1.5 text-sm leading-relaxed whitespace-pre-wrap text-tinta">{children}</div>
    </section>
  );
}

// ===========================================================================
// O vazio que DIZ o que fazer
// ===========================================================================
// "Nenhum resultado" sozinho é uma tela que não ensina nada. Aqui ela nomeia o
// recorte que produziu o vazio, que é o que a pessoa precisa desfazer.
export function SemResultado({ termo, aoLimpar }: { termo: string; aoLimpar: () => void }) {
  return (
    <div className="rounded-cf border border-borda bg-superficie-2 px-5 py-8 text-center" data-sem-resultado>
      <p className="text-sm text-tinta">
        Nenhum dispositivo do acervo casa com <strong>“{termo}”</strong>.
      </p>
      <p className="mt-1 text-sm text-cinza">
        A busca aqui procura o TEXTO. Para perguntar com as suas palavras, use o assistente —
        ele entende a pergunta, não só as palavras dela.
      </p>
      <button type="button" onClick={aoLimpar} className="pill-cta mt-4 inline-flex">
        Limpar a busca
      </button>
    </div>
  );
}
